import { useCallback, useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type {
	MediaEmojiProviderOptions,
	SyncBlockRendererProviderCreator,
} from '@atlaskit/editor-synced-block-provider';
import { EmojiResource } from '@atlaskit/emoji';
import {
	getConfluencePageAri,
	useMemoizedBlockServiceAPIProviders,
	useMemoizedSyncedBlockProvider,
} from '@atlaskit/editor-synced-block-provider';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { AnalyticsEventPayload } from '@atlaskit/editor-common/analytics';
import {
	getSyncBlockNodesFromDoc,
	type SyncedBlockNodeProps,
	useMemoizedSyncedBlockNodeComponent,
} from '@atlaskit/editor-synced-block-renderer';
import { CardClient } from '@atlaskit/link-provider';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';

import { fg } from '@confluence/feature-gating';
import { ConfluenceEditorCardProvider } from '@confluence/smartlinks';
import { createCookie } from '@confluence/content-smartlinks/entry-points/createCookie.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { createRendererMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { getMediaSSRModeAndAuthConfigMemoized } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers.ts';
import {
	useRouteActions,
	useMatchRoute,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

import { getEmojiProvider } from '../entry-points/EmojiProvider.ts';

import { createRelaySyncBlockFetchProvider } from './relaySyncBlockFetchProvider.ts';
import { createSmartCardEventHandler } from './utils/createSmartCardEventHandler.ts';
import { syncedBlockLoadSSRData } from './SyncedBlockLoadSSRData.ts';

export const useMemoizedSyncedBlockNestedRendererProviderCreator = (
	cloudId: string,
	userId: string | undefined | null,
): SyncBlockRendererProviderCreator => {
	return useMemo(
		() => ({
			createEmojiProvider: () => {
				return getEmojiProvider(EmojiResource, {
					cloudId,
					userId: userId || undefined,
					allowAnonymousAccess: true,
				});
			},
			createMediaProvider: ({ contentId: refSyncBlockContentId }: MediaEmojiProviderOptions) => {
				return createRendererMediaProvider({
					contentId: refSyncBlockContentId,
					isExternalShare: false,
				});
			},
			createSSRMediaProvider: ({ contentId: refSyncBlockContentId }: MediaEmojiProviderOptions) => {
				const mediaClientConfig = getMediaSSRModeAndAuthConfigMemoized({
					contentId: refSyncBlockContentId,
					isExternalShare: false,
				});

				if (mediaClientConfig?.mode === 'server') {
					return {
						viewMediaClientConfig: mediaClientConfig.config,
					};
				}

				return undefined;
			},
			// Uses the same logic as in preloadEditorNodeDataProvidersData.ts
			createSmartLinkProvider: () => {
				const { host, cloudId: ssrCloudId, userContext, customURLs } = getSSRRenderInputs();
				const objectResolverURL =
					customURLs?.['object-resolver-service']?.match(/^(https?:\/\/[^/]+)/)?.[0];

				if (objectResolverURL) {
					const ssrCardClient = new CardClient('custom', objectResolverURL);
					ssrCardClient.setHeaders({
						Cookie: createCookie(userContext),
						Origin: host.startsWith('http') ? host : `https://${host}`,
						CloudId: ssrCloudId || cloudId,
					});

					return Promise.resolve(
						new ConfluenceEditorCardProvider('custom', objectResolverURL, undefined, ssrCardClient),
					);
				}

				return Promise.resolve(new ConfluenceEditorCardProvider());
			},
		}),
		[cloudId, userId],
	);
};

type ProviderDefinitions = {
	[name: string]: Promise<any>;
};

/** Same shape as page sticky header config so tables in synced blocks use the same scroll root (e.g. AkMainContent). */
export type SyncedBlockStickyHeadersConfig = {
	offsetTop: number;
	defaultScrollRootId_DO_NOT_USE?: string;
	shouldAddDefaultScrollRootOffsetTop_DO_NOT_USE?: boolean;
};

export type SyncedBlockProviderNodeComponentProps = {
	doc: DocNode;
	contentId: string | undefined;
	contentType: string | undefined;
	mergedDataProviderDefinitions: ProviderDefinitions;
	/** When set, nested renderer uses this scroll root and offset so sticky table headers work on the page. */
	stickyHeadersConfig?: SyncedBlockStickyHeadersConfig;
};

const useMemoizedSyncedBlockProviderNodeComponentEnabled = ({
	doc,
	contentId: confluencePageId,
	contentType,
	mergedDataProviderDefinitions,
	stickyHeadersConfig,
}: SyncedBlockProviderNodeComponentProps):
	| ((props: SyncedBlockNodeProps) => React.JSX.Element)
	| undefined => {
	const syncBlockNodes = useMemo(() => {
		return getSyncBlockNodesFromDoc(doc);
	}, [doc]);
	const { cloudId, userId } = useSessionData();

	const confluencePageAri = useMemo(
		() =>
			confluencePageId
				? getConfluencePageAri({
						pageId: confluencePageId,
						cloudId,
						pageType: contentType === 'page' ? 'page' : 'blogpost',
					})
				: undefined,
		[cloudId, confluencePageId, contentType],
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fetchProvider: blockServiceFetchProvider, writeProvider: blockServiceWriteProvider } =
		useMemoizedBlockServiceAPIProviders({
			parentAri: confluencePageAri ?? '',
			parentId: confluencePageId ?? '',
			product: 'confluence-page',
			cloudId,
		});

	// Wrap the fetch provider with Relay subscription support
	const relayFetchProvider = useMemo(
		() => createRelaySyncBlockFetchProvider(blockServiceFetchProvider, cloudId),
		[blockServiceFetchProvider, cloudId],
	);

	const syncedBlockNestedRendererProviderCreator =
		useMemoizedSyncedBlockNestedRendererProviderCreator(cloudId, userId);
	const providerOptions = useMemo(() => {
		return {
			parentDataProviders: {
				...mergedDataProviderDefinitions,
			},
			providerCreator: syncedBlockNestedRendererProviderCreator,
		};
	}, [mergedDataProviderDefinitions, syncedBlockNestedRendererProviderCreator]);
	const syncedBlockProvider = useMemoizedSyncedBlockProvider({
		fetchProvider: relayFetchProvider,
		writeProvider: blockServiceWriteProvider,
		providerOptions,
		getSSRData: syncedBlockLoadSSRData,
	});

	const handleSyncedBlockAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: payload,
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const { push } = useRouteActions();
	const matchRoute = useMatchRoute();
	const syncedBlockEventHandlers = useMemo(() => {
		return createSmartCardEventHandler({ matchRoute, push });
	}, [matchRoute, push]);

	const SyncedBlockNodeComponent = useMemoizedSyncedBlockNodeComponent({
		syncBlockProvider: syncedBlockProvider,
		syncBlockNodes,
		syncBlockRendererOptions: {
			smartLinks: {
				ssr: true,
			},
			eventHandlers: syncedBlockEventHandlers,
			stickyHeaders: stickyHeadersConfig
				? { show: fg('platform_synced_block_patch_4'), ...stickyHeadersConfig }
				: { show: fg('platform_synced_block_patch_4'), offsetTop: 0 },
		},
		fireAnalyticsEvent: handleSyncedBlockAnalyticsEvent,
	});

	// If confluencePageId is undefined, don't return the synced block node component, and don't render synced blocks
	return confluencePageId ? SyncedBlockNodeComponent : undefined;
};

export const useMemoizedSyncedBlockProviderNodeComponentDisabled = (
	_props: SyncedBlockProviderNodeComponentProps,
): ((props: SyncedBlockNodeProps) => React.JSX.Element) | undefined => {
	return undefined;
};

export const useMemoizedSyncedBlockProviderNodeComponent: (
	props: SyncedBlockProviderNodeComponentProps,
) => ((props: SyncedBlockNodeProps) => React.JSX.Element) | undefined = conditionalHooksFactory(
	() => isExperimentEnabled('platform_synced_block'),
	useMemoizedSyncedBlockProviderNodeComponentEnabled,
	useMemoizedSyncedBlockProviderNodeComponentDisabled,
);
